import {
  CustomerReview,
  customerWorkflow,
  customerFollowUpExpired,
} from "./customerWorkflow";
export const apiBase =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:3201";
export class ReviewApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
  }
}
export async function reviewApi<T = any>(
  path: string,
  method = "GET",
  body?: unknown,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(`${apiBase}/api/reviews${path}`, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new ReviewApiError(
      response.status,
      result.message || result.error || "Could not complete this request"
    );
  return result;
}
export function jurisdictionLabel(j: any) {
  if (!j || j.level === "unsure") return "Jurisdiction to clarify";
  return [
    j.region,
    j.countryCode,
    j.level === "federal"
      ? "Federal"
      : j.level === "country"
        ? "Country-wide"
        : null,
  ]
    .filter(Boolean)
    .join(" · ");
}
export const statusLabel: Record<string, string> = {
  queued: "Awaiting assignment",
  assigned: "Assigned",
  in_progress: "In progress",
  completed: "Answer delivered",
  cancelled: "Cancelled",
};
/** e.g. "Sat, Sep 19, 11:09 PM EDT"; the year appears only when it is not the current one. */
export const dateLabel = (value: string) => {
  const date = new Date(value);
  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year:
      date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
};
/** A deadline with how far away it is, e.g. "Mon, Sep 28, 12:30 AM EDT (in 3 days)". */
export function dueLabel(value: string, now = Date.now()) {
  const ms = new Date(value).getTime() - now,
    hours = Math.round(Math.abs(ms) / 3_600_000),
    span =
      hours < 1
        ? "less than an hour"
        : hours < 48
          ? `${hours} hour${hours === 1 ? "" : "s"}`
          : `${Math.round(hours / 24)} days`;
  return `${dateLabel(value)} (${ms >= 0 ? `in ${span}` : `${span} ago`})`;
}
/** After the agreed follow-up window, a delivered review can only be closed. */
export const followUpEnded = (
  review: {
    completed_at?: string;
    clarification_deadline?: string | null;
  },
  now = Date.now()
) => !!review.completed_at && customerFollowUpExpired(review, now);
export const isOverdue = (
  review: {
    due_at?: string;
    status: string;
    closed_at?: string;
  },
  now = Date.now()
) =>
  !!review.due_at &&
  review.status === "in_progress" &&
  !review.closed_at &&
  new Date(review.due_at).getTime() < now;
export type ReviewSummary = {
  id: string;
  question_id: string;
  title: string;
  topic_key: string;
  jurisdiction: any;
  status: string;
  version: number;
  attorney_name: string | null;
  client_name: string;
  assigned_attorney_id: string | null;
  assigned_coverage: any;
  created_at: string;
  updated_at: string;
  latest_client_seq: string;
  attorney_replied_through: string;
  latest_attorney_seq: string;
  client_replied_through: string;
  snapshot_hash: string;
  proposal_state?: string;
  proposal_version?: number;
  payment_state?: string;
  /** Whether a card is saved for this review (no card details). */
  has_card?: boolean;
  agreed_at?: string;
  due_at?: string;
  completed_at?: string;
  closed_at?: string;
  clarification_deadline?: string | null;
  /** Attorney-side next step, computed by the server (see nextActionSql). */
  next_action?: NextAction;
  is_overdue?: boolean;
};
export type NextAction =
  | "accept"
  | "reply"
  | "draft"
  | "waiting_on_client"
  | "waiting_approval"
  | "waiting_payment"
  | "follow_up_open"
  | "closed"
  | "unassigned";
/** Mirrors the server's nextActionSql for rows that predate the field. */
export function nextActionFor(r: {
  next_action?: NextAction;
  status: string;
  closed_at?: string;
  proposal_state?: string;
  payment_state?: string;
  latest_client_seq?: string | number;
  attorney_replied_through?: string | number;
  latest_attorney_seq?: string | number;
  client_replied_through?: string | number;
}): NextAction {
  if (r.next_action) return r.next_action;
  if (r.closed_at || r.status === "cancelled") return "closed";
  if (r.status === "queued") return "unassigned";
  if (Number(r.latest_client_seq) > Number(r.attorney_replied_through))
    return "reply";
  if (r.proposal_state === "agreed" && r.payment_state === "awaiting_payment")
    return "waiting_payment";
  if (r.proposal_state === "awaiting_client") return "waiting_approval";
  if (r.status === "assigned") return "accept";
  if (r.status === "completed") return "follow_up_open";
  if (Number(r.latest_attorney_seq) > Number(r.client_replied_through))
    return "waiting_on_client";
  return "draft";
}
/** What the attorney needs to do, in the attorney's words. */
export function nextActionLabel(r: {
  next_action?: NextAction;
  is_overdue?: boolean;
  status: string;
  closed_at?: string;
}) {
  switch (r.next_action) {
    case "accept":
      return "Accept or propose changes";
    case "reply":
      return "Client replied";
    case "draft":
      return r.is_overdue ? "Overdue" : "In progress";
    case "waiting_on_client":
      return "Waiting on client";
    case "waiting_approval":
      return "Waiting for client approval";
    case "waiting_payment":
      return "Waiting for payment";
    case "follow_up_open":
      return "Answer delivered";
    default:
      return reviewStatusLabel(r);
  }
}
/** A concrete action alongside the queue's status, using the same next step. */
export function nextActionInstruction(
  r: Parameters<typeof nextActionFor>[0] & {
    completed_at?: string;
    clarification_deadline?: string | null;
  }
) {
  switch (nextActionFor(r)) {
    case "accept":
      return "Review and accept the terms, or propose changes";
    case "reply":
      return "Reply to the client";
    case "draft":
      return "Write and deliver your answer";
    case "waiting_on_client":
      return "Follow up with the client if needed";
    case "waiting_approval":
      return "Client approval is needed before work starts";
    case "waiting_payment":
      return "Client payment is needed to start the review";
    case "follow_up_open":
      return followUpEnded(r)
        ? "The follow-up window ended; close when finished"
        : "Review follow-up terms or close when finished";
    case "closed":
      return "View the review record";
    default:
      return "Awaiting attorney assignment";
  }
}
export const nextActionTone = (r: {
  next_action?: NextAction;
  is_overdue?: boolean;
}) =>
  r.next_action === "accept" || r.next_action === "reply"
    ? "action"
    : r.next_action === "draft" && r.is_overdue
      ? "overdue"
      : r.next_action === "follow_up_open" || r.next_action === "closed"
        ? "done"
        : r.next_action === "draft"
          ? "working"
          : "waiting";

export const topicLabel = (value: string) =>
  (value || "General")
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
export const shortDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
export const initials = (value: string) =>
  (value || "R")
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0])
    .join("")
    .toUpperCase();

/** True until work starts; an agreement still awaiting payment has not started. */
export function reviewNotStarted(review: {
  status: string;
  proposal_state?: string;
  payment_state?: string;
}) {
  return (
    ["queued", "assigned"].includes(review.status) &&
    (review.proposal_state !== "agreed" ||
      review.payment_state === "awaiting_payment")
  );
}

export function reviewStatusLabel(review: {
  status: string;
  proposal_state?: string;
  payment_state?: string;
  closed_at?: string;
}) {
  if (review.closed_at && review.status !== "cancelled") return "Closed";
  if (["completed", "cancelled"].includes(review.status))
    return statusLabel[review.status];
  if (
    review.proposal_state === "agreed" &&
    review.payment_state === "awaiting_payment"
  )
    return "Awaiting payment";
  if (review.proposal_state === "awaiting_client")
    return "Awaiting client approval";
  if (review.proposal_state === "declined") return "Changes declined";
  if (review.proposal_state === "needs_revision")
    return "Proposal needs updating";
  if (
    review.status === "assigned" &&
    review.proposal_state === "awaiting_attorney"
  )
    return "Awaiting attorney acceptance";
  return statusLabel[review.status];
}

/** One vocabulary for the question list, question tab, and review. */
export function customerStatusLabel(review: CustomerReview) {
  return customerWorkflow(review).label;
}

/**
 * Payment standing for the admin queue, so requests whose customer never saved
 * a card stand out. Null when no payment applies.
 */
export function adminPaymentLabel(review: {
  payment_state?: string;
  has_card?: boolean;
  status: string;
  closed_at?: string;
}): { text: string; unpaid: boolean } | null {
  if (review.payment_state === "refunding") return {text:"Refund pending",unpaid:false};
  if (review.payment_state === "refunded") return {text:"Refunded",unpaid:false};
  if (review.status === "cancelled" || review.closed_at) return null;
  if (review.payment_state === "paid") return { text: "Paid", unpaid: false };
  if (review.payment_state === "unpaid")
    return review.has_card
      ? { text: "Card saved · charged on acceptance", unpaid: false }
      : { text: "Not paid · no card saved", unpaid: true };
  if (review.payment_state === "awaiting_payment")
    return {
      text: review.has_card
        ? "Not paid · card charge failed"
        : "Not paid · no card saved",
      unpaid: true,
    };
  return null;
}
